/*
 * Императивные уведомления и подтверждения вместо системных alert/confirm.
 * Состояние живёт в модуле без React — ToastHost только подписывается.
 * Одновременно показывается не больше одной плашки: новая заменяет текущую.
 */

let state = { toast: null, confirm: null };
const listeners = new Set();

let hideTimer = null;  // авто-исчезновение через toastObj.duration (5с info/success, 7с error)
let leaveTimer = null; // финальное скрытие через 0.2 сек после ухода
let hostReady = false; // true, когда ToastHost смонтирован и resumeToasts() зван

function emit() {
  listeners.forEach((fn) => {
    try {
      fn(state);
    } catch (_) {
      // Ошибка одного слушателя не должна ломать показ для остальных
    }
  });
}

function clearTimers() {
  clearTimeout(hideTimer);
  clearTimeout(leaveTimer);
  hideTimer = null;
  leaveTimer = null;
}

function beginLeave(toastObj) {
  // Уход: плашка плавно гаснет 0.2 сек, только потом исчезает из состояния
  clearTimeout(hideTimer);
  state = { ...state, toast: { ...toastObj, leaving: true } };
  emit();
  leaveTimer = setTimeout(() => {
    if (state.toast && state.toast.id === toastObj.id) {
      state = { ...state, toast: null };
      emit();
    }
  }, 200);
}

function armHide(toastObj) {
  // До монтирования хоста таймер не заводим — иначе плашка может истечь невидимой
  clearTimeout(hideTimer);
  if (!hostReady) return;
  hideTimer = setTimeout(() => {
    if (state.toast === toastObj) beginLeave(toastObj);
  }, toastObj.duration);
}

// Длительность показа по виду: ошибки читают дольше, статусы — быстрее
const TOAST_DURATIONS = {
  error: 7000,
  info: 5000,
  success: 5000,
};

function show(kind, message) {
  clearTimers();
  const toastObj = {
    id: `${Date.now()}-${Math.random()}`,
    kind,
    // Не-строки (например, detail-массив из 422-ответа FastAPI) крашат React-рендер
    message: typeof message === 'string' ? message : String(message),
    leaving: false,
    duration: TOAST_DURATIONS[kind] || TOAST_DURATIONS.info,
  };
  state = { ...state, toast: toastObj };
  emit();
  armHide(toastObj);
}

export function toast(message) {
  show('info', message);
}
toast.info = (message) => show('info', message);
toast.error = (message) => show('error', message);
toast.success = (message) => show('success', message);

export function confirmDialog(message, {
  okLabel = 'Да',
  cancelLabel = 'Нет',
  danger = false,
  detail = null,
  onConfirm = null,
} = {}) {
  // Без хоста пользователь ничего не увидит — безопасный ответ «нет»
  if (!hostReady) return Promise.resolve(false);
  // Новое подтверждение перебивает старое: старое закрывается как «нет»
  if (state.confirm) state.confirm.resolve(false);
  let resolveFn;
  const promise = new Promise((resolve) => { resolveFn = resolve; });
  state = {
    ...state,
    confirm: {
      message, okLabel, cancelLabel, danger, detail, onConfirm,
      busy: false,
      resolve: resolveFn,
    },
  };
  emit();
  return promise;
}

export function resolveConfirm(value) {
  if (!state.confirm || state.confirm.busy) return; // во время busy закрыть нельзя
  const confirmObj = state.confirm;
  if (value && confirmObj.onConfirm) {
    // Подтверждение принято: модалка остаётся открытой и показывает «...»,
    // пока операция не завершится — мутируем на месте, чтобы ссылка
    // confirmObj оставалась той же для проверки в finishConfirm
    confirmObj.busy = true;
    state = { ...state, confirm: confirmObj }; // новый top-level объект => перерисовка
    emit();
    Promise.resolve()
      .then(() => confirmObj.onConfirm())
      .then(() => finishConfirm(confirmObj, true))
      .catch(() => finishConfirm(confirmObj, false)); // ошибка уже показана вызывающим
    return;
  }
  finishConfirm(confirmObj, value);
}

function finishConfirm(confirmObj, value) {
  // Подтверждение могли перебить, пока работал onConfirm — тогда state уже другой
  if (state.confirm !== confirmObj) return;
  state = { ...state, confirm: null };
  emit();
  confirmObj.resolve(value);
}

export function dismissToast() {
  // Закрытие по тапу: если плашка уже уходит — повторный тап игнорируем
  if (!state.toast || state.toast.leaving) return;
  beginLeave(state.toast);
}

export function subscribe(fn) {
  listeners.add(fn);
  fn(state); // первый вызов — сразу текущее состояние (для инициализации хоста)
  return () => listeners.delete(fn);
}

export function resumeToasts() {
  // Хост смонтирован: показанная ранее плашка получает свой таймер
  hostReady = true;
  if (state.toast && !state.toast.leaving) armHide(state.toast);
}

// Только для тестов: полный сброс модуля между кейсами
export function __resetForTests() {
  clearTimers();
  state = { toast: null, confirm: null };
  hostReady = false;
  listeners.clear();
}
